import { ISeed } from '../pages/SeedPage/SeedPage.types';
import { NewShelfBlock } from '../types/models';
import { linearFeetOfBlocks, PogKind } from './contract';
import { PegxDocumentContext } from './context';
import { setPegxDocumentState } from './pegxDocument';
import { configureTransport, setLogoutListener } from './transport';
import { MerchGuideDownloadInput, SeedDownloadInput, SeedViewInput, tracker } from './tracker';

export type { DownloadType, MerchGuideSource, PogKind } from './contract';
export type { MerchGuideDownloadInput, SeedDownloadInput, SeedViewInput } from './tracker';
export { createTelemetryMiddleware } from './middleware';

/** Called once from index.tsx. Before this, every track* call is a no-op on the network. */
export const startTelemetry = (baseUrl: string | null | undefined): void => {
  configureTransport(baseUrl);
  setLogoutListener(() => tracker.destroy());
  tracker.start();
};

export const trackSeedDownload = (input: SeedDownloadInput): void => tracker.trackSeedDownload(input);

export const trackSeedView = (input: SeedViewInput): void => tracker.trackSeedView(input);

export const trackMerchGuideDownload = (input: MerchGuideDownloadInput): void =>
  tracker.trackMerchGuideDownload(input);

/** PEGx: storeNum / jobId are known only from the opened PDF's metadata (contract §4.1). */
export const setPegxDocumentContext = (value: PegxDocumentContext | null): void => {
  setPegxDocumentState(value);
  tracker.refresh();
};

type SeedKindFields = Pick<ISeed, 'isProto' | 'isOptimized' | 'isSeed'> & { jobId?: string };

/** UI-only knowledge, never derived by the backend (contract §3.1). */
export const getSeedPogKind = ({ isProto, isOptimized, isSeed, jobId }: SeedKindFields): PogKind => {
  if (isProto) {
    return jobId ? 'EDITED_PROTO' : 'PDF_PROTO';
  }

  if (isOptimized) {
    return 'OPTIMIZED';
  }

  return isSeed ? 'SHARED_POG' : 'PRIVATE_EDITED';
};

export type SeedDownloadFields = Pick<
  SeedDownloadInput,
  'jobId' | 'aisleCode' | 'pogKind' | 'pogLinearSize' | 'targetLinearMetric' | 'productivityEstimate'
>;

/**
 * Seed-page attributes of a SeedDownload: the seed's own linear, the live Aisle Dimensions linear
 * (both in feet, computed the same way) and the seed's productivity estimate (pog_score).
 * A missing estimate stays null, so the tracker omits the attribute instead of sending 0.
 */
export const getSeedDownloadFields = (
  seed: Pick<ISeed, 'isProto' | 'isOptimized' | 'isSeed' | 'aisleCode' | 'linear' | 'productivityEstimate'>
    & { jobId?: string },
  dimension?: NewShelfBlock[] | null,
): SeedDownloadFields => ({
  jobId: seed.jobId || '',
  aisleCode: seed.aisleCode,
  pogKind: getSeedPogKind(seed),
  pogLinearSize: seed.linear,
  targetLinearMetric: linearFeetOfBlocks(dimension),
  productivityEstimate: seed.productivityEstimate,
});
